// MobileDeckEditorPage — Deck-Editor für Fenster bis 900px Breite (Weiche in
// DeckEditorRoute.js). Eigenes Layout, geteilte Logik: dieselben Editor-Hooks wie die
// Desktop-Page, aber kein DnD, keine Kommandobrücke, kein Desktop-Chrome
// (Entwurf: docs/superpowers/specs/2026-08-17-mobile-deck-editor-design.md).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ListPlus, Plus, SearchX } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import Page from '../../../ui/Page';
import { useToast } from '../../../ui/Toast';
import ConfirmDialog from '../../../ui/ConfirmDialog';
import CardViewer from '../../../ui/CardViewer';
import EmptyState from '../../../ui/EmptyState';
import { formatCategoryName } from '../../../ui/formatCategoryName';
import { json } from '../../../api/apiClient';
import CardDetail from '../../../components/CardDetail';
import { useAuth } from '../../../context/AuthContext';
import { useEditorPreferences } from '../../../hooks/useEditorPreferences';
import { useUpdateCardMetadata } from '../../../hooks/useWantlistData';
import { getStrategy } from '../utils/groupingStrategies';
import { computeDeckTotals } from '../utils/formatPrice';
import { deckNumericId } from '../utils/deckIds';
import { useDeckViewState } from '../hooks/useDeckViewState';
import { useDeckCardActions, mergeCategoryIntoDeckCache } from '../hooks/useDeckCardActions';
import { useDeckLabels } from '../hooks/useDeckLabels';
import { useDeckUndo } from '../hooks/useDeckUndo';
import { useDeckSyntaxSearch } from '../hooks/useDeckSyntaxSearch';
import { useShouldBeTransparent } from '../hooks/useShouldBeTransparent';
import { visibleCategoryOrder, findCategoryNeighbor } from './utils/categoryOrder';
import { buildViewerList } from './utils/viewerList';
import { useHistoryLayer } from './hooks/useHistoryLayer';
import { useCardSelection } from './hooks/useCardSelection';
import { useCloseOnBack } from '../../../ui/backLayer/useCloseOnBack';
import { useBackLayerManager } from '../../../ui/backLayer/backLayerContext';
import { useMobileDeckActions, formatCardCount } from './hooks/useMobileDeckActions';
import DeckExportModal from '../components/DeckExportModal';
import AddCategoryDialog from '../components/AddCategoryDialog';
import NewLabelModal from '../components/NewLabelModal';
import MobileEditorHeader from './components/MobileEditorHeader';
import MobileStatsPage from './components/MobileStatsPage';
import MobileFilterStrip from './components/MobileFilterStrip';
import MobileAddMode from './components/MobileAddMode';
import MobileCategorySection from './components/MobileCategorySection';
import MobileViewerActions from './components/MobileViewerActions';
import CategoryPickerSheet from './components/CategoryPickerSheet';
import LabelPickerSheet from './components/LabelPickerSheet';
import SelectedCardsSheet from './components/SelectedCardsSheet';
import SelectionBar from './components/SelectionBar';
// Die Label-Punkt-Klassen von LabelIndicators liegen in der Feature-Stylesheet.
import '../DeckEditor.css';
import './MobileDeckEditor.css';

/**
 * Deck-Zweig der Seite: Kategorie-Sektionen mit ihren Kartenrastern. Eigene
 * Komponente, damit Effekte, die nur im sichtbaren Deck laufen dürfen, an ihrem
 * Mount hängen können — die Page selbst bleibt über alle Vollbild-Ebenen gemountet.
 */
function MobileDeckBody({
  groups,
  columnCount,
  stickyTop,
  highlightedCardId,
  highlightNonce,
  getCardLabels,
  selectionMode = false,
  selectedCardIds = null,
  onCardTap,
  onCardLongPress,
  isOwnProfile = false,
  strategy,
  categories = [],
  onRenameCategory,
  onMoveCategory,
  onRequestDeleteCategory,
  isSearchActive = false,
}) {
  // Sichtliste für „Nach oben"/„Nach unten": categories enthält die Commander-Kategorie
  // ungefiltert, sie zählt hier nie als Nachbar und blockiert nie ein Ende (categoryOrder.js).
  const visibleCategories = useMemo(() => visibleCategoryOrder(categories), [categories]);

  // Zur markierten Karte scrollen — nach dem Hinzufügen-Modus oder einem Rücksprung beim
  // Schließen des Viewers. Der Ring selbst kommt aus der `highlighted`-Prop der Kachel und
  // läuft unabhängig davon; hier wird nur ENTSCHIEDEN, ob zusätzlich gescrollt werden muss.
  // Der Effekt gehört hierher und nicht in den „✕ Fertig"-Handler: dessen `close()` ist ein
  // History-Schritt, dieser Zweig entsteht erst danach — ein sofortiger Lookup fände den
  // Knoten nicht. Beim Remount ist die Kennung bereits gesetzt, deshalb feuert er hier
  // zuverlässig. `highlightNonce` in den Abhängigkeiten, weil derselbe Rücksprung zweimal
  // hintereinander auf dieselbe Karte sonst keinen erkennbaren Wechsel der Kennung wäre.
  useEffect(() => {
    if (!highlightedCardId) return undefined;
    // Messen und scrollen erst im nächsten Frame: Beide Rücksprung-Wege entstehen aus einem
    // Chronik-Schritt (`navigate(-1)`), und Chromium legt die gespeicherte Scroll-Position des
    // Ziel-Eintrags erst NACH dem popstate-Ereignis auf. Ein Scroll aus diesem Effekt heraus
    // würde davon überschrieben (und ein weicher Scroll zusätzlich vom Chronik-Schritt selbst
    // abgebrochen), und die Sichtbarkeitsprüfung rechnete mit der noch nicht
    // wiederhergestellten Position. Im Frame darauf steht die Position endgültig.
    const frame = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-card-id="${highlightedCardId}"]`);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const stil = getComputedStyle(document.documentElement);
      // App-Header und ProfileBanner (Fremd-Profil) kleben über der Editor-Kopfzeile — dieselbe
      // Summe, mit der ui/CategoryHeader.js den Sticky-Versatz der Kategorie-Köpfe bildet.
      // Ohne sie gälte eine Karte im Band dahinter als sichtbar, obwohl der App-Header sie deckt.
      const kopfzeilenHoehe =
        (parseFloat(stil.getPropertyValue('--app-header-offset')) || 0)
        + (parseFloat(stil.getPropertyValue('--profile-banner-offset')) || 0)
        + stickyTop;
      // Drei Token: bei zweizeiligem Dock (Editor-Aktionen über der Filterzeile) kommt die
      // Bar-Höhe dazu, plus der Abstand der schwebenden Leiste zum unteren Rand —
      // dieselbe Summe, die ui/DetailSidePanel.css bildet.
      const dockHoehe =
        (parseFloat(stil.getPropertyValue('--filter-strip-dock-height')) || 0)
        + (parseFloat(stil.getPropertyValue('--filter-strip-dock-bar-height')) || 0)
        + (parseFloat(stil.getPropertyValue('--filter-strip-dock-gap')) || 0);
      const imBlick = rect.top >= kopfzeilenHoehe && rect.bottom <= window.innerHeight - dockHoehe;
      if (!imBlick) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => cancelAnimationFrame(frame);
  }, [highlightedCardId, highlightNonce, stickyTop]);

  if (groups.length === 0 && isSearchActive) {
    return (
      <div className="mobile-deck-body">
        <EmptyState
          variant="filter"
          icon={SearchX}
          title="Keine Karten gefunden"
          description="Passe Suche oder Filter an."
        />
      </div>
    );
  }

  return (
    <div className="mobile-deck-body">
      {groups.map((group) => {
        // Nur editierbare, echte Kategorien des eigenen Decks bekommen das ⋮-Menü —
        // nie die Commander-Sektion, nie eine Kategorie ohne persistierte ID (z.B. der
        // 'other'-Sammel-Fallback für unkategorisierte Karten).
        const canManageCategory = Boolean(
          isOwnProfile && strategy?.editable !== false && !group.isCommander && group.id != null
        );
        const visibleIndex = canManageCategory
          ? visibleCategories.findIndex((c) => c.name === group.name)
          : -1;
        return (
          <MobileCategorySection
            key={group.isCommander ? 'commander' : `${group.type}:${group.name}`}
            group={group}
            columnCount={columnCount}
            stickyTop={stickyTop}
            highlightedCardId={highlightedCardId}
            getCardLabels={getCardLabels}
            selectionMode={selectionMode}
            selectedCardIds={selectedCardIds}
            onCardTap={onCardTap}
            onCardLongPress={onCardLongPress}
            canManageCategory={canManageCategory}
            canMoveUp={visibleIndex > 0}
            canMoveDown={visibleIndex !== -1 && visibleIndex < visibleCategories.length - 1}
            onRenameCategory={onRenameCategory}
            onMoveCategory={onMoveCategory}
            onRequestDeleteCategory={onRequestDeleteCategory}
          />
        );
      })}
    </div>
  );
}

export default function MobileDeckEditorPage({ selectedDeck, isOwnProfile, onDeckUpdate }) {
  const { currentUser } = useAuth();
  const { push: pushToast } = useToast();
  const queryClient = useQueryClient();
  const { mobileColumnCount, setMobileColumnCount } = useEditorPreferences(currentUser?.id);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [groupingMode, setGroupingMode] = useState('categories');
  const [showExcluded, setShowExcluded] = useState(true);
  // Ring an einer Karte als Wiederfindungs-Hinweis (frisch hinzugefügt, oder Rücksprung
  // beim Schließen des Viewers). Ein Zähler neben der Kennung: zweimal dieselbe Kennung
  // hintereinander wäre sonst kein erkennbarer Wechsel für den auslösenden Effekt.
  const [highlightRequest, setHighlightRequest] = useState({ id: null, nonce: 0 });
  const requestHighlight = useCallback((id) => {
    setHighlightRequest((prev) => ({ id, nonce: prev.nonce + 1 }));
  }, []);
  // Für den Rücksprung beim Schließen des Viewers (siehe Viewer-Abschnitt): welche Karte
  // zuletzt zu sehen war, und ob der Viewer im vorigen Durchlauf offen war.
  const letzteViewerKarteRef = useRef(null);
  const warOffenRef = useRef(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  // Token-Modus, mit dem das Export-Modal öffnet: 'none' (⋮-Menü) bzw. 'only'
  // (Export-Button der Token-Kachel der Statistik-Seite).
  const [exportTokenMode, setExportTokenMode] = useState('none');
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [pendingDeleteCategory, setPendingDeleteCategory] = useState(null); // { id, name } | null
  // Ziel einer Kategorie-Auswahl bzw. Label-Vergabe: { scope: 'card', card } für die
  // Viewer-Aktionszeile, { scope: 'selection' } für den Auswahl-Modus.
  const [categoryPickerTarget, setCategoryPickerTarget] = useState(null);
  const [pendingMoveAfterCategory, setPendingMoveAfterCategory] = useState(null);
  const [labelPickerTarget, setLabelPickerTarget] = useState(null);
  const [newLabelTarget, setNewLabelTarget] = useState(null);
  const [detail, setDetail] = useState(null); // { scryfallId, oracleId, section, fallback }
  const [isSelectionSheetOpen, setIsSelectionSheetOpen] = useState(false);
  const [pendingBatchRemove, setPendingBatchRemove] = useState(null); // Karten-Array | null
  const [viewerIndex, setViewerIndex] = useState(0);
  // Ablage-Ziel des Hinzufügen-Modus: 'auto' (Server ordnet nach Kartentyp ein) oder eine
  // Kategorie-ID. Sitzungslokal je Deck — nichts wird persistiert.
  const [targetCategoryId, setTargetCategoryId] = useState('auto');
  const [lastAddedCardId, setLastAddedCardId] = useState(null);

  const canEditDeck = Boolean(isOwnProfile && selectedDeck && selectedDeck.source !== 'physical');

  const showError = useCallback((message) => {
    if (message) pushToast({ tone: 'error', message });
  }, [pushToast]);

  const cardActions = useDeckCardActions({
    deck: selectedDeck,
    canEdit: canEditDeck,
    canMove: isOwnProfile && Boolean(selectedDeck),
    onDeckUpdate,
    onError: showError,
  });

  const strategy = getStrategy(groupingMode);
  const { groups, categories, addCategory, renameCategory, moveCategory, removeCategory } = useDeckViewState({
    deck: selectedDeck,
    sortBy,
    strategy,
    showExcluded,
    isOwnProfile,
    includeCommander: true,
  });

  const matchingCardIds = useDeckSyntaxSearch({ deckId: selectedDeck?.id, searchTerm });
  const shouldBeTransparent = useShouldBeTransparent({
    highlightedCmc: null,
    searchTerm,
    matchingCardIds,
  });

  const { deckLabels, getCardLabels, assignLabel, removeLabel, createAndAssignLabel } = useDeckLabels(
    selectedDeck?.id,
    selectedDeck?.editorSettings?.labels || [],
    selectedDeck?.cards || [],
    showError,
  );
  const undo = useDeckUndo({
    deckId: selectedDeck?.id,
    cardActions,
    assignLabel,
    getCardLabels,
  });
  const actions = useMobileDeckActions({ cardActions, undo, pushToast, deck: selectedDeck, getCardLabels });

  const {
    isOpen: isViewerOpen,
    payload: viewerPayload,
    open: openViewer,
    close: closeViewer,
  } = useHistoryLayer('viewer');
  const {
    isOpen: isAddModeOpen,
    open: openAddMode,
    close: closeAddMode,
  } = useHistoryLayer('add');
  const {
    isOpen: isStatsOpen,
    open: openStats,
    close: closeStats,
  } = useHistoryLayer('stats');
  const selection = useCardSelection(selectedDeck?.id);
  // Zurück beendet den Auswahl-Modus, statt den Editor zu verlassen.
  useCloseOnBack(selection.active, selection.clear);
  const { closeLayersDownTo } = useBackLayerManager();

  // Über die Kaskade, nicht über ein einzelnes „einen Schritt zurück": der Toast ist auch
  // antippbar, während der Viewer über den Suchtreffern offen ist (zwei gestapelte Ebenen).
  // Ein einzelner Schritt schlösse dann nur den Viewer und markierte eine Karte in einem
  // nicht gerenderten Zweig. `closeLayersDownTo` nimmt die Add-Ebene samt allem darüber.
  const zeigeKarteImDeck = useCallback((cardId) => {
    closeLayersDownTo('add');
    requestHighlight(cardId);
  }, [closeLayersDownTo, requestHighlight]);

  // Ein Deckwechsel entwertet die Kategorie-ID des Ablage-Ziels. Ein Deckwechsel remountet
  // die Seite nicht — nur ein Routen-Parameter ändert sich —, deshalb müssen auch eine noch
  // laufende Hervorhebung und die beiden Viewer-Rücksprung-Refs mit zurückgesetzt werden:
  // sonst hinge eine 5-Sekunden-Markierung kurz an einer gleichnamigen Karte im neuen Deck,
  // und der Viewer gälte als „war offen, ist zu" mit einer Kennung aus dem alten Deck.
  useEffect(() => {
    setTargetCategoryId('auto');
    setLastAddedCardId(null);
    setHighlightRequest({ id: null, nonce: 0 });
    letzteViewerKarteRef.current = null;
    warOffenRef.current = false;
  }, [selectedDeck?.id]);

  // Der Ring an einer Karte ist ein Wiederfindungs-Hinweis, kein Zustand — er verschwindet
  // von selbst. Der Zähler bleibt beim Zurücksetzen unverändert, sonst löste das selbst
  // wieder einen (leeren) Wechsel aus.
  useEffect(() => {
    if (!highlightRequest.id) return undefined;
    const timer = setTimeout(
      () => setHighlightRequest((prev) => ({ id: null, nonce: prev.nonce })),
      5000
    );
    return () => clearTimeout(timer);
    // highlightRequest.id nicht als Abhängigkeit: der Effekt soll nur beim Wechsel der
    // Kennung neu starten (nonce), nicht wenn dieser Effekt selbst sie auf null setzt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightRequest.nonce]);

  // Kategorie-Köpfe kleben unter der Kopfzeile: deren gemessene Höhe ist ihr Versatz
  // (die App-Chrome-Offsets rechnet ui/CategoryHeader selbst dazu). Die Abhängigkeit vom
  // Hinzufügen-Modus ist nötig, weil die Kopfzeile nur im Deck-Zweig existiert: nach
  // seinem Remount steht ein neuer Knoten da, den der alte Observer nicht kennt.
  // getBoundingClientRect().height statt offsetHeight: offsetHeight rundet auf ganze
  // Pixel, die Kopfzeile sitzt aber auf dem subpixelgenauen --app-header-offset — die
  // Rundungsdifferenz liefe sonst als Schlitz zwischen Kopfzeile und Kategorie-Kopf mit.
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const update = () => setHeaderHeight(el.getBoundingClientRect().height);
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isAddModeOpen]);

  const excludedCategoryNames = useMemo(() => {
    const cats = selectedDeck?.editorSettings?.categories;
    if (!Array.isArray(cats)) return new Set();
    return new Set(cats.filter((c) => c.excluded).map((c) => c.name?.toLowerCase()).filter(Boolean));
  }, [selectedDeck?.editorSettings?.categories]);

  // Karten ohne „Nicht im Deck"-Kategorien — Grundlage für den Kartenzähler der
  // Kopfzeile UND die Statistik-Seite (Manakurve, Ziehchancen, Mana-Basis).
  const statsCards = useMemo(
    () => (selectedDeck?.cards || []).filter(
      (c) => !excludedCategoryNames.has(c.category?.toLowerCase() ?? '')
    ),
    [selectedDeck?.cards, excludedCategoryNames]
  );

  const deckTotals = useMemo(() => computeDeckTotals(statsCards), [statsCards]);

  const targetCount = useMemo(
    () => ((selectedDeck?.cards || []).some((c) => c.isCommander) ? 100 : null),
    [selectedDeck?.cards]
  );

  const isSearchActive = Boolean(searchTerm.trim());
  const visibleGroups = useMemo(() => {
    const result = [];
    for (const group of groups) {
      const cards = (group.cards || []).filter((card) => !shouldBeTransparent(card));
      // Bei aktiver Suche verschwinden Kategorien ohne Treffer ganz; ohne Suche
      // bleiben auch leere sichtbar, damit eine frisch angelegte sofort auftaucht.
      if (isSearchActive && cards.length === 0) continue;
      result.push({ ...group, cards });
    }
    return result;
  }, [groups, shouldBeTransparent, isSearchActive]);

  // ── Viewer ───────────────────────────────────────────────────────────────
  // Die Ebene liegt in der History (Zurück-Geste schließt sie), der laufende Index
  // dagegen im lokalen State: Wischen soll keine History-Einträge erzeugen. Der Viewer
  // blättert durch alle sichtbaren Gruppen als eine Liste; die Chronik-Nutzlast bleibt
  // „Kategorie + Position darin", damit ein Reload wieder bei derselben Karte landet.
  const viewerList = useMemo(() => buildViewerList(visibleGroups), [visibleGroups]);
  // Zugriff aus dem Öffnen-Effekt, ohne die Liste als Abhängigkeit zu führen: sonst risse
  // eine Karten-Änderung während des Blätterns (z.B. Mengen-Änderung im Viewer) den Index
  // auf die ursprüngliche Öffnen-Position zurück.
  const viewerListRef = useRef(viewerList);
  viewerListRef.current = viewerList;

  const viewerCards = useMemo(
    () => viewerList.cards.map((card) => ({
      ...card,
      imageUrlBack: card.imageUrlBack || undefined,
    })),
    [viewerList]
  );
  const viewerCard = viewerCards[Math.min(viewerIndex, viewerCards.length - 1)] || null;

  useEffect(() => {
    if (isViewerOpen) {
      setViewerIndex(viewerListRef.current.indexOf(viewerPayload?.groupName, Number(viewerPayload?.index) || 0));
    }
    // Nicht `viewerPayload` selbst als Abhängigkeit: react-router liest den Chronik-Zustand
    // bei jedem Zurücknehmen neu aus der Chronik, das Objekt ist danach ein anderes. Der
    // Effekt liefe sonst nach dem Schließen jeder Fläche über dem Viewer (Sheet, Dialog)
    // erneut und setzte die aufgeblätterte Position auf die Öffnen-Position zurück.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewerOpen, viewerPayload?.groupName, viewerPayload?.index]);

  // Entfernen und Menge-auf-0 verkürzen die deckweite Liste unter dem offenen Viewer:
  // Index klemmen, erst bei leerer Liste die Ebene schließen — die letzte Karte einer
  // Kategorie zu entfernen zeigt also die nächste Gruppe, statt den Viewer zu schließen.
  // Der Ref-Wächter verhindert ein zweites navigate(-1), solange der History-Pop noch
  // nicht angekommen ist — das würde den Editor selbst verlassen.
  const viewerClosingRef = useRef(false);
  useEffect(() => {
    if (!isViewerOpen) {
      viewerClosingRef.current = false;
      return;
    }
    if (viewerCards.length === 0) {
      if (!viewerClosingRef.current) {
        viewerClosingRef.current = true;
        closeLayersDownTo('viewer');
      }
      return;
    }
    if (viewerIndex > viewerCards.length - 1) setViewerIndex(viewerCards.length - 1);
  }, [isViewerOpen, closeLayersDownTo, viewerCards.length, viewerIndex]);

  // Solange der Viewer offen ist, mitschreiben, welche Karte zu sehen war …
  useEffect(() => {
    if (isViewerOpen && viewerCard) letzteViewerKarteRef.current = viewerCard.scryfallId || viewerCard.id;
  }, [isViewerOpen, viewerCard]);

  // … und beim Übergang „offen → zu" dorthin zurückspringen. Ein Handler reicht nicht: die
  // Zurück-Geste schließt die Ebene über die Chronik, ohne einen aufzurufen — isViewerOpen
  // folgt aus dem Location-State, der Übergang muss also beobachtet werden.
  useEffect(() => {
    if (warOffenRef.current && !isViewerOpen && letzteViewerKarteRef.current) {
      requestHighlight(letzteViewerKarteRef.current);
    }
    warOffenRef.current = isViewerOpen;
  }, [isViewerOpen, requestHighlight]);

  // ── Auswahl-Modus ────────────────────────────────────────────────────────
  const canMoveCards = Boolean(isOwnProfile && selectedDeck && strategy.editable);

  const selectedCards = useMemo(() => {
    if (!selection.active || selection.count === 0) return [];
    return (selectedDeck?.cards || []).filter((card) => selection.ids.has(card.scryfallId || card.id));
  }, [selection.active, selection.count, selection.ids, selectedDeck?.cards]);

  // Endet der Modus, darf das Auswahl-Sheet nicht als „offen" stehenbleiben — es käme
  // sonst beim nächsten Long-Press ungefragt hoch.
  useEffect(() => {
    if (!selection.active) setIsSelectionSheetOpen(false);
  }, [selection.active]);

  const handleCardTap = useCallback((card, group) => {
    const cardId = card.scryfallId || card.id;
    if (selection.active) {
      selection.toggle(cardId);
      return;
    }
    const cards = group?.cards || [];
    const index = cards.findIndex((c) => (c.scryfallId || c.id) === cardId);
    openViewer({ groupName: group?.name ?? null, index: index < 0 ? 0 : index });
  }, [selection, openViewer]);

  const handleCardLongPress = useCallback((card) => {
    const cardId = card.scryfallId || card.id;
    if (selection.active) selection.toggle(cardId);
    else selection.start(cardId);
  }, [selection]);

  // ── Verschieben (einzeln und im Batch) ───────────────────────────────────
  const runMove = useCallback(async (target, categoryId, categoryName) => {
    if (!target) return;
    if (target.scope === 'card') {
      if (target.card) await actions.moveSingle(target.card, categoryId, categoryName);
      return;
    }
    const cards = selectedCards;
    selection.clear();
    const ergebnis = await actions.moveBatch(cards, categoryId, categoryName);
    // Wiederholen soll ein Tipp sein, keine neue Suche: was gescheitert ist, bleibt markiert.
    if (ergebnis?.failedIds?.length) selection.restore(ergebnis.failedIds);
  }, [actions, selectedCards, selection]);

  // Eine gewählte Kategorie bedeutet je Ziel etwas anderes: der Hinzufügen-Modus merkt
  // sie als Ablage-Ziel, alle anderen Aufrufer verschieben Karten dorthin.
  const applyCategoryChoice = useCallback(async (target, categoryId, categoryName) => {
    if (!target) return;
    if (target.scope === 'add-target') {
      // `null` ist die Typ-Fallback-Wahl des Servers — im Wähler heißt sie „Automatisch".
      setTargetCategoryId(categoryId ?? 'auto');
      return;
    }
    await runMove(target, categoryId, categoryName);
  }, [runMove]);

  const handleCategorySelected = useCallback(async (value) => {
    const target = categoryPickerTarget;
    setCategoryPickerTarget(null);
    const categoryId = value === 'auto' ? null : value;
    const category = categories.find((c) => (c.id ?? null) === categoryId);
    await applyCategoryChoice(target, categoryId, category?.name);
  }, [categoryPickerTarget, categories, applyCategoryChoice]);

  // „Neue Kategorie…" im Auswahl-Sheet: Dialog öffnen und das Verschieben-Ziel merken,
  // damit die frisch angelegte Kategorie direkt das Ziel wird. Nur bei canEditDeck
  // verdrahtet (siehe Callsite) — sonst käme ein physisches Deck über das
  // Verschieben-Sheet (canMoveCards, unabhängig von canEditDeck) an „＋ Kategorie" vorbei.
  const handleCreateCategoryForMove = useCallback(() => {
    setPendingMoveAfterCategory(categoryPickerTarget);
    setCategoryPickerTarget(null);
    setIsAddCategoryOpen(true);
  }, [categoryPickerTarget]);

  // ── Labels ──────────────────────────────────────────────────────────────
  const applyLabelToCards = useCallback(async (cards, label) => {
    const assignable = cards.filter((card) => card.deckCardEntryId);
    const missing = assignable.filter(
      (card) => !getCardLabels(card.deckCardEntryId).some((l) => l.id === label.id)
    );
    for (const card of missing) {
      await assignLabel(card.deckCardEntryId, label);
    }
    if (missing.length > 0) {
      pushToast({
        tone: 'success',
        message: `Label „${label.name}" für ${formatCardCount(missing.length)} gesetzt`,
      });
    } else if (assignable.length > 0) {
      pushToast({ tone: 'info', message: `Label „${label.name}" liegt schon an allen markierten Karten` });
    } else {
      showError('Für diese Karten sind keine Labels möglich.');
    }
  }, [assignLabel, getCardLabels, pushToast, showError]);

  const handleLabelSelected = useCallback(async (label) => {
    const target = labelPickerTarget;
    setLabelPickerTarget(null);
    if (!target) return;
    const cards = target.scope === 'card' ? [target.card] : selectedCards;
    if (target.scope === 'selection') selection.clear();
    await applyLabelToCards(cards, label);
  }, [labelPickerTarget, selectedCards, selection, applyLabelToCards]);

  const handleCreateLabel = useCallback(async (name, color) => {
    const target = newLabelTarget;
    setNewLabelTarget(null);
    if (!target) return;
    const cards = target.scope === 'card' ? [target.card] : selectedCards;
    if (target.scope === 'selection') selection.clear();
    // Angelegt wird das Label an der ersten Karte, die eine Deck-Zeile hat; die
    // übrigen bekommen es danach zugewiesen (createAndAssignLabel kennt nur eine Karte).
    const first = cards.find((card) => card?.deckCardEntryId);
    if (!first) {
      showError('Für diese Karten sind keine Labels möglich.');
      return;
    }
    const result = await createAndAssignLabel(name, color, first.deckCardEntryId);
    if (!result?.ok) {
      showError(result?.error || 'Label konnte nicht erstellt werden.');
      return;
    }
    const rest = cards.filter((card) => card !== first);
    if (rest.length > 0) await applyLabelToCards(rest, result.label);
  }, [newLabelTarget, selectedCards, selection, createAndAssignLabel, applyLabelToCards, showError]);

  // ── Batch-Aktionen ──────────────────────────────────────────────────────
  // Die Auswahl endet, sobald die Aktion läuft: die sequenziellen Batches brauchen
  // mehrere Sekunden, und die Leiste darf in der Zeit keine zweite Runde auf denselben
  // Karten auslösen.
  const handleBatchQuantity = useCallback(async (delta) => {
    const cards = selectedCards;
    selection.clear();
    const ergebnis = await actions.quantityBatch(cards, delta);
    // Wiederholen soll ein Tipp sein, keine neue Suche: was gescheitert ist, bleibt markiert.
    if (ergebnis?.failedIds?.length) selection.restore(ergebnis.failedIds);
  }, [selectedCards, selection, actions]);

  const handleConfirmBatchRemove = useCallback(async () => {
    const cards = pendingBatchRemove || [];
    setPendingBatchRemove(null);
    selection.clear();
    const ergebnis = await actions.removeBatch(cards);
    // Wiederholen soll ein Tipp sein, keine neue Suche: was gescheitert ist, bleibt markiert.
    if (ergebnis?.failedIds?.length) selection.restore(ergebnis.failedIds);
  }, [pendingBatchRemove, selection, actions]);

  // ── Viewer-Aktionen ─────────────────────────────────────────────────────
  // Optimistisches Kategorie-Update für setAsCommander (Muster
  // DeckEditorPage.handleCategoryChange).
  const applyCategoryChange = useCallback((cardId, categoryLike) => {
    onDeckUpdate?.((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        cards: (prev.cards || []).map((card) => (
          (card.scryfallId || card.id) === cardId
            ? {
                ...card,
                categoryId: categoryLike?.id ?? null,
                category: categoryLike?.name ?? null,
                isCommander: !!categoryLike?.isCommander,
              }
            : card
        )),
      };
    });
  }, [onDeckUpdate]);

  const handleToggleCommander = useCallback(async (card, makeCommander) => {
    await cardActions.setAsCommander(card.scryfallId || card.id, makeCommander, applyCategoryChange);
  }, [cardActions, applyCategoryChange]);

  // Zeile „Commander" im Kategorie-Sheet: Verschieben in die Commander-Kategorie,
  // einzeln aus dem Viewer oder für die ganze Auswahl.
  const handleCommanderSelected = useCallback(async () => {
    const target = categoryPickerTarget;
    setCategoryPickerTarget(null);
    if (!target) return;
    const cards = target.scope === 'card' ? [target.card].filter(Boolean) : selectedCards;
    if (target.scope === 'selection') selection.clear();
    const ergebnis = await actions.moveToCommander(cards, applyCategoryChange);
    // Wiederholen soll ein Tipp sein, keine neue Suche: was gescheitert ist, bleibt markiert.
    if (target.scope === 'selection' && ergebnis?.failedIds?.length) selection.restore(ergebnis.failedIds);
  }, [categoryPickerTarget, selectedCards, selection, actions, applyCategoryChange]);

  const { mutate: updateCardMetadata } = useUpdateCardMetadata();

  // Die lebende Editor-Karte zum geöffneten Modal; nach einem Print-Wechsel unter der neuen ID.
  const detailCard = useMemo(() => (detail
    ? (selectedDeck?.cards || []).find((c) => (c.scryfallId || c.id) === detail.scryfallId) ?? null
    : null), [detail, selectedDeck]);

  const openCardDetail = useCallback((card, section = 'card') => {
    if (!card?.oracleId) return;
    setDetail({
      scryfallId: card.scryfallId || card.id,
      oracleId: card.oracleId,
      section,
      fallback: { oracleId: card.oracleId, name: card.name, typeLine: card.typeLine, imageUrl: card.imageUrl, imageUrlBack: card.imageUrlBack || undefined, imageOrientation: card.imageOrientation },
    });
  }, []);
  const handleShowDetails = useCallback((card) => openCardDetail(card, 'card'), [openCardDetail]);
  const handleSwitchPrint = useCallback((card) => openCardDetail(card, 'prints'), [openCardDetail]);
  const handleUnlockPrint = useCallback(() => {
    const entryId = detailCard?.deckCardEntryId;
    if (!entryId) return;
    // Fehler meldet MutationErrorToasts
    updateCardMetadata(
      { deckCardId: entryId, metadata: { printLocked: false } },
      { onSuccess: () => pushToast({ tone: 'success', message: 'Print freigegeben' }) },
    );
  }, [detailCard, updateCardMetadata, pushToast]);

  const commitDeckRename = useCallback(async (nextName) => {
    if (!canEditDeck || !selectedDeck || !nextName) return;
    const previousName = selectedDeck.name;
    onDeckUpdate?.((prev) => (prev ? { ...prev, name: nextName } : prev));
    try {
      await json(`/api/decks/${selectedDeck.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newName: nextName }),
      });
    } catch (err) {
      console.error('Fehler beim Aktualisieren des Decknamens:', err);
      onDeckUpdate?.((prev) => (prev ? { ...prev, name: previousName } : prev));
      showError('Deckname konnte nicht geändert werden.');
    }
  }, [canEditDeck, selectedDeck, onDeckUpdate, showError]);

  // Kam der Dialog aus einem Kategorie-Auswahl-Sheet, ist die neue Kategorie sofort das
  // Verschieben-Ziel — sonst müsste der Nutzer das Sheet erneut öffnen.
  const handleAddCategory = useCallback(async (name, excluded = false) => {
    const category = await addCategory?.(name, excluded);
    // Alle Mutationen melden Fehler sichtbar — ein stumm fehlgeschlagenes Anlegen sieht aus
    // wie ein Klick, der nicht angekommen ist.
    if (!category) {
      showError('Kategorie konnte nicht angelegt werden.');
      return;
    }
    onDeckUpdate?.((prev) => (prev ? mergeCategoryIntoDeckCache(prev, category) : prev));
    // Das Ziel steckt in dieser Closure, nicht im State — es überlebt damit das
    // Zurücksetzen beim Schließen des Dialogs.
    if (pendingMoveAfterCategory) {
      await applyCategoryChoice(pendingMoveAfterCategory, category.id, category.name);
    }
  }, [addCategory, onDeckUpdate, pendingMoveAfterCategory, applyCategoryChoice, showError]);

  // Nach erfolgreichem Kategorie-Rename: Deck-Cache nachziehen (Karten referenzieren
  // die Kategorie per Name; editorSettings.categories speist excludedCategoryNames) —
  // analog DeckEditorPage.handleCategoryRenamed.
  const handleRenameCategory = useCallback(async (group, newName) => {
    const result = await renameCategory?.(group.id, newName);
    if (result?.ok && result.category) {
      const updated = result.category;
      onDeckUpdate?.((prev) => {
        if (!prev) return prev;
        const cards = (prev.cards || []).map((card) =>
          card.categoryId === updated.id ||
          (card.category && card.category.toLowerCase() === group.name.toLowerCase())
            ? { ...card, categoryId: updated.id, category: updated.name }
            : card
        );
        const savedCats = prev.editorSettings?.categories;
        const nextCategories = Array.isArray(savedCats)
          ? savedCats.map((c) => (c.id === updated.id ? { ...c, name: updated.name } : c))
          : savedCats;
        return { ...prev, cards, editorSettings: { ...prev.editorSettings, categories: nextCategories } };
      });
    } else if (result?.conflict) {
      showError('Name bereits vergeben');
    } else if (result) {
      showError('Kategorie konnte nicht umbenannt werden.');
    }
  }, [renameCategory, onDeckUpdate, showError]);

  // „Nach oben"/„Nach unten": Nachbar in der Sichtliste (ohne Commander) bestimmen und
  // per Namen tauschen — die Reihenfolge-Persistenz läuft automatisch über den
  // Debounce-Effekt in useCategoryManagement, kein eigener Request nötig.
  const handleMoveCategory = useCallback((group, direction) => {
    const neighbor = findCategoryNeighbor(categories, group.name, direction);
    if (!neighbor) return;
    moveCategory?.(group.name, neighbor.name);
  }, [categories, moveCategory]);

  const handleRequestDeleteCategory = useCallback((group) => {
    setPendingDeleteCategory({ id: group.id, name: group.name });
  }, []);

  const pendingDeleteCount = useMemo(() => {
    if (!pendingDeleteCategory) return 0;
    return (selectedDeck?.cards || []).filter(
      (c) => (c.category || '').toLowerCase() === pendingDeleteCategory.name.toLowerCase()
    ).length;
  }, [pendingDeleteCategory, selectedDeck?.cards]);

  // Löscht die Kategorie MIT ihren Karten — Vorgehen an useDeckDragHandlers.js
  // (Desktop-Löschpfad) ausgerichtet: Server-Delete zuerst, dann Deck-Cache und lokale
  // Kategorieliste nachziehen (removeCategory setzt den Tombstone, der einen erneuten
  // Sync aus dem — hier bereits bereinigten — Deck-Cache verhindert) und die
  // Wantlist-Kennzahlen invalidieren, damit gelöschte Karten dort nicht als „fehlend"
  // hängen bleiben.
  const handleConfirmDeleteCategory = useCallback(async () => {
    const target = pendingDeleteCategory;
    setPendingDeleteCategory(null);
    if (!target?.id) return;
    try {
      await json(`/api/decks/${selectedDeck.id}/categories/${target.id}`, { method: 'DELETE' });
    } catch (error) {
      console.error('Fehler beim Löschen der Kategorie:', error);
      showError('Kategorie konnte nicht gelöscht werden.');
      return;
    }
    onDeckUpdate?.((prev) => {
      if (!prev) return prev;
      const cards = (prev.cards || []).filter(
        (c) => (c.category || '').toLowerCase() !== target.name.toLowerCase()
      );
      const savedCats = prev.editorSettings?.categories;
      const nextCategories = Array.isArray(savedCats)
        ? savedCats.filter((c) => c.id !== target.id)
        : savedCats;
      return { ...prev, cards, editorSettings: { ...prev.editorSettings, categories: nextCategories } };
    });
    removeCategory?.(target.name);
    queryClient.invalidateQueries({ queryKey: ['wantlist'] });
  }, [pendingDeleteCategory, selectedDeck?.id, onDeckUpdate, showError, removeCategory, queryClient]);

  // ── Hinzufügen-Modus ────────────────────────────────────────────────────
  // Gemeinsamer Add-Pfad aller drei Wege (＋-Ecke, Viewer-Knopf, Kandidaten-Batch).
  // `target` läuft als Parameter, nie über den State: die Batch-Schleife friert ihn beim
  // Start ein. `silent` unterdrückt alle Toasts UND den Freunde-Abruf — im Batch wären
  // das n Info-Toasts und n Zusatz-Anfragen; die Undo-Registrierung bleibt.
  const addEntry = useCallback(async (entry, { silent = false, target = targetCategoryId } = {}) => {
    const categoryArg = target === 'auto' ? {} : { categoryId: target };
    const result = await cardActions.addCardByScryfallId(entry.id, categoryArg);
    if (result?.card) {
      const entryId = undo.registerAdd(result.card);
      setLastAddedCardId(result.card.id);
      if (!silent) {
        pushToast({
          tone: 'success',
          message: `${result.card.name} hinzugefügt`,
          action: { label: 'Rückgängig', onClick: () => undo.undoEntry(entryId) },
          // Der Tipp auf den Text macht dasselbe wie „✕ Fertig", aber für GENAU diese Karte.
          onMessageClick: () => zeigeKarteImDeck(result.card.id),
        });
        json(`/api/wantlist/friends-ownership/${entry.id}`).then(({ friends }) => {
          if (friends.length) {
            pushToast({
              tone: 'info',
              message: `${friends[0].name} besitzt diese Karte (${friends[0].quantity}×)${friends.length > 1 ? ` — und ${friends.length - 1} weitere` : ''}`,
            });
          }
        }).catch(() => {});
      }
      return { entryId, duplicate: false };
    }
    if (result?.duplicate) {
      if (!silent) pushToast({ tone: 'info', message: `${entry.name} ist bereits im Deck` });
      return { duplicate: true };
    }
    // Die Fehler-Ursache ist aus der null-Rückgabe nicht ablesbar (z.B. inzwischen
    // gelöschte Ziel-Kategorie) — ein toter Zielwert darf nicht kleben bleiben. Im
    // silent-Pfad regelt das der Batch-Aufrufer nach Ende der Schleife.
    if (!silent && target !== 'auto') setTargetCategoryId('auto');
    return null;
  }, [targetCategoryId, cardActions, undo, pushToast, zeigeKarteImDeck]);

  const targetCategoryLabel = useMemo(() => {
    if (targetCategoryId === 'auto') return 'Automatisch';
    const name = categories.find((c) => c.id === targetCategoryId)?.name;
    return name ? formatCategoryName(name) : 'Automatisch';
  }, [targetCategoryId, categories]);

  // Der Handler macht NUR Schließen und Markieren — den Scroll übernimmt der Effekt in
  // MobileDeckBody, der erst nach dem History-Schritt mit dem Zweig neu entsteht.
  const handleFinishAdd = useCallback(() => {
    closeAddMode();
    if (lastAddedCardId) requestHighlight(lastAddedCardId);
  }, [closeAddMode, lastAddedCardId, requestHighlight]);

  const handleOpenAddCard = useCallback(() => {
    setLastAddedCardId(null);
    openAddMode();
  }, [openAddMode]);

  const handleOpenTokenExport = useCallback(() => {
    setExportTokenMode('only');
    setIsExportOpen(true);
  }, []);

  const editorRow = canEditDeck ? (
    <div className="mobile-editor-dock">
      <button
        type="button"
        className="btn-secondary mobile-editor-dock__action"
        onClick={handleOpenAddCard}
      >
        <Plus size={16} aria-hidden /> Karte
      </button>
      {strategy.editable && (
        <button
          type="button"
          className="btn-secondary mobile-editor-dock__action"
          onClick={() => setIsAddCategoryOpen(true)}
        >
          <ListPlus size={16} aria-hidden /> Kategorie
        </button>
      )}
    </div>
  ) : null;

  const selectionRow = (
    <SelectionBar
      count={selection.count}
      canEdit={canEditDeck}
      canMove={canMoveCards}
      onMove={() => setCategoryPickerTarget({ scope: 'selection' })}
      onLabel={() => setLabelPickerTarget({ scope: 'selection' })}
      onQuantityDelta={handleBatchQuantity}
      onDelete={() => setPendingBatchRemove(selectedCards)}
    />
  );

  return (
    <Page width="full" className="mobile-deck-editor">
      {/* Der Hinzufügen-Modus tritt AN DIE STELLE des Deck-Zweigs, statt darüber zu
          liegen: zwei gedockte Filterleisten stapelten sich sonst unkontrolliert am
          unteren Rand. */}
      {isAddModeOpen ? (
        <MobileAddMode
          deckId={selectedDeck.id}
          numericDeckId={deckNumericId(selectedDeck)}
          deckCards={selectedDeck.cards}
          canEditDeck={canEditDeck}
          targetLabel={targetCategoryLabel}
          onOpenTargetPicker={() => setCategoryPickerTarget({ scope: 'add-target' })}
          onDone={handleFinishAdd}
          addEntry={addEntry}
          targetCategoryId={targetCategoryId}
          onResetTarget={() => setTargetCategoryId('auto')}
          undo={undo}
          zeigeKarteImDeck={zeigeKarteImDeck}
        />
      ) : (
        <>
          <MobileEditorHeader
            ref={headerRef}
            deckName={selectedDeck.name}
            isPhysical={isOwnProfile && selectedDeck.source === 'physical'}
            canEditDeck={canEditDeck}
            cardCount={deckTotals.count}
            targetCount={targetCount}
            onRename={commitDeckRename}
            onOpenStats={() => openStats()}
            onExport={() => { setExportTokenMode('none'); setIsExportOpen(true); }}
            columnCount={mobileColumnCount}
            onColumnCountChange={setMobileColumnCount}
            showExcluded={showExcluded}
            onToggleShowExcluded={() => setShowExcluded((prev) => !prev)}
            selectionMode={selection.active}
            selectedCount={selection.count}
            onCancelSelection={selection.clear}
            onShowSelection={() => setIsSelectionSheetOpen(true)}
          />

          <MobileDeckBody
            groups={visibleGroups}
            columnCount={mobileColumnCount}
            stickyTop={headerHeight}
            highlightedCardId={highlightRequest.id}
            highlightNonce={highlightRequest.nonce}
            getCardLabels={getCardLabels}
            selectionMode={selection.active}
            selectedCardIds={selection.ids}
            onCardTap={handleCardTap}
            onCardLongPress={isOwnProfile ? handleCardLongPress : undefined}
            isOwnProfile={isOwnProfile}
            strategy={strategy}
            categories={categories}
            onRenameCategory={handleRenameCategory}
            onMoveCategory={handleMoveCategory}
            onRequestDeleteCategory={handleRequestDeleteCategory}
            isSearchActive={isSearchActive}
          />

          <MobileFilterStrip
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            groupingMode={groupingMode}
            onGroupingModeChange={setGroupingMode}
            dockBar={selection.active ? selectionRow : editorRow}
          />
        </>
      )}

      <CardViewer
        open={isViewerOpen}
        cards={viewerCards}
        index={viewerIndex}
        onIndexChange={setViewerIndex}
        onClose={closeViewer}
        label={viewerList.labelFor(Math.min(viewerIndex, Math.max(viewerCards.length - 1, 0)))}
      >
        {viewerCard && (
          <MobileViewerActions
            card={viewerCard}
            isOwnProfile={isOwnProfile}
            canEdit={canEditDeck}
            canMove={canMoveCards}
            deckLabels={deckLabels}
            cardLabels={viewerCard?.deckCardEntryId ? getCardLabels(viewerCard.deckCardEntryId) : []}
            onQuantityDelta={actions.quantityDelta}
            onMove={(card) => setCategoryPickerTarget({ scope: 'card', card })}
            onAssignLabel={(label) => assignLabel(viewerCard.deckCardEntryId, label)}
            onRemoveLabel={(labelId) => removeLabel(viewerCard.deckCardEntryId, labelId)}
            onNewLabel={() => setNewLabelTarget({ scope: 'card', card: viewerCard })}
            onShowDetails={handleShowDetails}
            onSwitchPrint={handleSwitchPrint}
            onToggleCommander={handleToggleCommander}
            onRemove={actions.removeSingle}
          />
        )}
      </CardViewer>

      <CategoryPickerSheet
        open={Boolean(categoryPickerTarget)}
        onOpenChange={(next) => { if (!next) setCategoryPickerTarget(null); }}
        categories={categories}
        onSelect={handleCategorySelected}
        // Nur der Hinzufügen-Modus kennt „Automatisch": beim Verschieben würde die Zeile
        // dasselbe bedeuten wie der Typ-Fallback und wäre eine zweite Formulierung dafür.
        showAutomatic={categoryPickerTarget?.scope === 'add-target'}
        // Gleiches Recht wie „Als Commander setzen" im ⋮-Menü (canEditDeck, nicht
        // canMoveCards); als Ablage-Ziel des Hinzufügen-Modus gibt es keinen Commander.
        onSelectCommander={canEditDeck
          && categoryPickerTarget
          && categoryPickerTarget.scope !== 'add-target'
          && !(categoryPickerTarget.scope === 'card' && categoryPickerTarget.card?.isCommander)
          ? handleCommanderSelected
          : undefined}
        onCreateNew={canEditDeck ? handleCreateCategoryForMove : undefined}
        title={categoryPickerTarget?.scope === 'selection'
          ? `${formatCardCount(selection.count)} verschieben`
          : categoryPickerTarget?.scope === 'add-target'
            ? 'Karten ablegen in'
            : 'Kategorie wählen'}
      />

      <LabelPickerSheet
        open={Boolean(labelPickerTarget)}
        onOpenChange={(next) => { if (!next) setLabelPickerTarget(null); }}
        labels={deckLabels}
        onSelect={handleLabelSelected}
        onCreateNew={() => {
          setNewLabelTarget(labelPickerTarget);
          setLabelPickerTarget(null);
        }}
      />

      <SelectedCardsSheet
        open={isSelectionSheetOpen && selection.active}
        onOpenChange={setIsSelectionSheetOpen}
        cards={selectedCards}
        onRemoveFromSelection={selection.toggle}
      />

      {newLabelTarget && (
        <NewLabelModal
          onConfirm={handleCreateLabel}
          onClose={() => setNewLabelTarget(null)}
        />
      )}

      <CardDetail
        open={!!detail}
        onOpenChange={(next) => { if (!next) setDetail(null); }}
        oracleId={detail?.oracleId}
        scryfallId={detail?.scryfallId}
        fallback={detail?.fallback ?? null}
        section={detail?.section}
        context="switch-print"
        canAct={canEditDeck}
        printLocked={!!detailCard?.printLocked}
        thumbnailOpensViewer={false}
        onSwitchPrint={async (newScryfallId) => {
          const ok = await cardActions.replaceCardPrint(detail.scryfallId, newScryfallId);
          if (ok) {
            pushToast({ tone: 'success', message: 'Print gewechselt' });
            setDetail((cur) => (cur ? { ...cur, scryfallId: newScryfallId } : cur));
          }
          return ok;
        }}
        onUnlockPrint={handleUnlockPrint}
      />

      <ConfirmDialog
        open={Boolean(pendingBatchRemove?.length)}
        onOpenChange={(next) => { if (!next) setPendingBatchRemove(null); }}
        onConfirm={handleConfirmBatchRemove}
        title={`${formatCardCount((pendingBatchRemove || []).length)} entfernen?`}
        description="Die Karten werden mit ihrer gesamten Menge aus dem Deck entfernt."
        confirmLabel="Entfernen"
        tone="danger"
      />

      <AddCategoryDialog
        open={isAddCategoryOpen}
        onOpenChange={(next) => {
          setIsAddCategoryOpen(next);
          // Beim Schließen — auch beim Abbrechen — verfällt ein gemerktes
          // Verschieben-Ziel, sonst würde die nächste angelegte Kategorie es erben.
          if (!next) setPendingMoveAfterCategory(null);
        }}
        onAdd={handleAddCategory}
      />
      <ConfirmDialog
        open={!!pendingDeleteCategory}
        onOpenChange={(next) => { if (!next) setPendingDeleteCategory(null); }}
        onConfirm={handleConfirmDeleteCategory}
        title="Kategorie löschen"
        description={`Die Kategorie und ihre ${formatCardCount(pendingDeleteCount)} ${pendingDeleteCount === 1 ? 'wird' : 'werden'} entfernt.`}
        confirmLabel="Löschen"
        tone="danger"
      />
      <DeckExportModal
        deck={selectedDeck}
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        excludedCategoryNames={excludedCategoryNames}
        initialTokenMode={exportTokenMode}
        isOwnProfile={isOwnProfile}
      />

      {isStatsOpen && (
        <MobileStatsPage
          deck={selectedDeck}
          statsCards={statsCards}
          excludedCategoryNames={excludedCategoryNames}
          targetCount={targetCount}
          deckTotals={deckTotals}
          canEditDeck={canEditDeck}
          isOwnProfile={isOwnProfile}
          onDeckUpdate={onDeckUpdate}
          onClose={closeStats}
          onExportTokens={handleOpenTokenExport}
          onOpenCard={handleShowDetails}
          cardActions={cardActions}
          undo={undo}
        />
      )}
    </Page>
  );
}
